import {
  Box,
  Icon,
  HStack,
  Stack,
  Text,
  Slide,
  LayoutProps,
} from '@chakra-ui/react';
import { usePathname } from 'next/navigation';
import { ICONS } from '~/constants/icons';
import { ROUTES } from '@habity/constants';
import { useUser } from '~/hooks/useUser';
import {
  isBuyLinkActive,
  isSearchHistoryLinkActive,
  isSellLinkActive,
  isHomeSearchLinkActive,
  isBuyerSearchLinkActive,
  isBrokerSearchLinkActive,
} from '~/lib/menus';
import { useUIStore } from '~/store';
import { Loader } from '~/components/ui/Loader';
import { getSellPath } from '@habity/services';
import { LinkWithFilterReset } from '../Link';

interface NavButtonProps {
  label: string;
  icon: any;
  iconSize?: LayoutProps['boxSize'];
  isActive: boolean;
  onClick?: (...args: any) => void;
  href?: string;
}

const NavButton = ({
  label,
  icon,
  iconSize = '24px',
  isActive,
  onClick,
  href,
}: NavButtonProps) => {
  const Component = () => (
    <Box cursor="pointer" onClick={onClick}>
      <Stack justifyContent="center" alignItems="center" spacing="1">
        <Icon
          focusable
          as={icon}
          boxSize={iconSize}
          color={isActive ? 'blackAlpha.900' : 'blackAlpha.600'}
        />
        <Text
          fontSize="10px"
          fontWeight="semibold"
          color={isActive ? 'blackAlpha.900' : 'blackAlpha.600'}
          textAlign="center"
        >
          {label}
        </Text>
      </Stack>
    </Box>
  );

  if (href) {
    return (
      <LinkWithFilterReset href={href}>
        <Component />
      </LinkWithFilterReset>
    );
  }

  return <Component />;
};

interface BottomNavigationProps {
  isOpen?: boolean;
}

export const BottomNavigation = ({ isOpen = true }: BottomNavigationProps) => {
  const modals = useUIStore((state) => state.modals);
  const setModals = useUIStore((state) => state.setModals);
  const { loginOpen } = modals;
  const { user } = useUser();
  const pathname = usePathname();

  if (!pathname) return <Loader />;

  return (
    <Slide direction="bottom" in={isOpen} style={{ zIndex: 10 }}>
      <HStack
        borderTopWidth="1px"
        borderTopColor="blackAlpha.200"
        backgroundColor="#FDF9F7"
        px="4"
        zIndex="overlay"
        h="72px"
        pb="12px"
        position="sticky"
        bottom="0"
        justify="center"
        boxShadow="sm"
      >
        {/* Buttons */}
        <HStack
          flex="1"
          spacing="2"
          justifyContent="space-between"
          alignItems="flex-start"
          maxW="300px"
        >
          <NavButton
            label="Bostäder"
            icon={
              (isHomeSearchLinkActive(pathname) ||
                isBuyerSearchLinkActive(pathname)) &&
                !loginOpen
                ? ICONS.SEARCH_ACTIVE
                : ICONS.SEARCH
            }
            isActive={
              (isHomeSearchLinkActive(pathname) ||
                isBuyerSearchLinkActive(pathname)) &&
              !loginOpen
            }
            href={ROUTES.SEARCH.HOMES}
          />

          {user ? (
            // Logged-in only
            <>
              {/* <NavButton
                label="Mina hem"
                icon={
                  isHomesLinkActive(pathname) && !loginOpen
                    ? ICONS.MY_HOMES_ACTIVE
                    : ICONS.MY_HOMES
                }
                isActive={isHomesLinkActive(pathname) && !loginOpen}
                href={ROUTES.DASHBOARD.SELLER.INDEX}
              /> */}

              <NavButton
                label="Bevakningar"
                icon={
                  isSearchHistoryLinkActive(pathname) && !loginOpen
                    ? ICONS.SEARCHES_ACTIVE
                    : ICONS.SEARCHES
                }
                isActive={isSearchHistoryLinkActive(pathname) && !loginOpen}
                href={ROUTES.DASHBOARD.BUYER.INDEX}
              />
            </>
          ) : (
            // Logged-out only
            <>
              <NavButton
                label="Bevaka"
                icon={
                  isBuyLinkActive(pathname) && !loginOpen
                    ? ICONS.BUY_ACTIVE
                    : ICONS.BUY
                }
                isActive={isBuyLinkActive(pathname) && !loginOpen}
                href={ROUTES.BUY}
              />

              <NavButton
                label="Värdera"
                icon={
                  isSellLinkActive(pathname) && !loginOpen
                    ? ICONS.SELL_ACTIVE
                    : ICONS.SELL
                }
                isActive={isSellLinkActive(pathname) && !loginOpen}
                href={getSellPath(undefined, 'valuation')}
              />
            </>
          )}

          <NavButton
            label="Mäklare"
            icon={
              isBrokerSearchLinkActive(pathname) && !loginOpen
                ? ICONS.BROKER_ACTIVE
                : ICONS.BROKER
            }
            isActive={isBrokerSearchLinkActive(pathname) && !loginOpen}
            href={ROUTES.SEARCH.BROKERS}
          />

          <NavButton
            label="Mer"
            icon={ICONS.MORE}
            isActive={false}
            onClick={() => setModals({ ...modals, mobileMenuOpen: true })}
          />
        </HStack>
      </HStack>
    </Slide>
  );
};
